import { loadAllowlist, getAllowlistExamples } from './soloFlagService';
import { groqChatCompletion } from './groqClient';

// Difficulty-adjusted scoring rubric shown to the AI
const RUBRIC = {
  kafi: `
Score generously — this is easy mode:
- 0: invalid (wrong letter, wrong category, blank, not a real word)
- 5-7: very common answer (most people would say this)
- 8-10: somewhat uncommon (requires some thought)
- 11-13: uncommon (good knowledge needed)
- 14-15: rare or exceptional answer`,

  kosi: `
Score fairly — this is medium mode:
- 0: invalid (wrong letter, wrong category, blank, not a real word)
- 3-5: ultra-common answer (over half of players would say this)
- 6-8: common (20-50% of players)
- 9-11: uncommon (5-20% of players)
- 12-13: rare (1-5% of players)
- 14-15: exceptional (under 1% of players)`,

  kemi: `
Score strictly — this is hard mode, show no mercy on common answers:
- 0: invalid (wrong letter, wrong category, blank, not a real word)
- 1-2: ultra-common (over 50% would say this — very low score)
- 3-5: common (20-50% of players)
- 6-8: uncommon (5-20% of players)
- 9-11: rare (1-5% of players)
- 12-15: exceptional (under 1% of players)`,
};

/**
 * Reviews both user and bot answers, returning variable scores 0-15 per category.
 *
 * @param {string}   letter      - Current round letter
 * @param {string[]} categories  - e.g. ['name', 'animal', 'place', 'thing']
 * @param {Object}   userAnswers - { [category]: string }
 * @param {Object}   botAnswers  - { [category]: string }
 * @param {string}   botKey      - 'kafi' | 'kosi' | 'kemi' (sets scoring difficulty)
 * @returns {Promise<{ user: Object, bot: Object }>}
 *   Each value is { [category]: number } where 0 = invalid, 1-15 = scored
 */
export async function reviewAnswers(letter, categories, userAnswers, botAnswers, botKey = 'kosi') {
  const rubric = RUBRIC[botKey] || RUBRIC.kosi;

  // Load admin-approved word allowlist to inject as context
  const allowlist = await loadAllowlist().catch(() => ({}));
  const allowlistLines = categories
    .map((c) => {
      const words = getAllowlistExamples(allowlist, letter, c);
      return words.length ? `  ${c}: ${words.join(', ')}` : null;
    })
    .filter(Boolean)
    .join('\n');

  const fmt = (answers) =>
    categories
      .map((c) => `  ${c}: "${answers[c]?.trim() || ''}"`)
      .join('\n');

  const exampleScores = categories.map((c) => `"${c}": 0`).join(', ');

  const prompt = `You are scoring answers for a word game called NameIt. The current letter is "${letter}".
${allowlistLines ? `\nAdmin-confirmed valid answers for letter ${letter}:\n${allowlistLines}\nThese must NEVER score 0.\n` : ''}
CRITICAL — Cultural awareness:
This game is played globally. Accept answers from ANY culture, language or region as valid:
- Nigerian names (Yoruba, Igbo, Hausa etc.) are valid: Olamide, Oluwaseun, Ngozi, Emeka, Aminu
- African places are valid: Ogun, Osun, Enugu, Nairobi, Ouagadougou
- Arabic, Indian, Asian, Latin American names and places are all valid
- If an answer is a real name/place/animal/thing in ANY culture or language, it counts
- Do NOT penalise answers just because they are uncommon in Western contexts
- When in doubt about a culturally specific answer, give it the benefit of the doubt

Spelling / typo rules (apply to player answers only, not bot):
- Perfect spelling: score normally.
- Minor typo (1–2 characters off, word is immediately recognisable — e.g. "Venezuala" for "Venezuela"): deduct 2–3 points from the base score but still pass. Do NOT score 0.
- Moderate misspelling (recognisable with effort — e.g. "Venuezuela"): deduct 3–5 points but still pass.
- Heavy misspelling (barely recognisable) or phonetic guess that could be a different word entirely: score 0.
- Completely unrecognisable nonsense: score 0.
- IMPORTANT: The game is timed so poor spelling should carry a real cost. Do not be too lenient — a heavily misspelled answer that just happens to start with the right letter should fail. Reserve partial credit for answers where the intended word is unambiguous.

Rules:
1. Every answer must start with the letter "${letter}" — if it doesn't, score 0.
2. The answer must be a real, valid example of its category in ANY world culture — if not, score 0.
3. Blank answers always score 0.
4. If the player and bot gave the SAME valid answer for a category, halve BOTH scores (round down).
${rubric}

Player answers:
${fmt(userAnswers)}

Bot answers:
${fmt(botAnswers)}

Return ONLY valid JSON with integer scores:
{
  "user": { ${exampleScores} },
  "bot":  { ${exampleScores} }
}`;

  try {
    const raw = await groqChatCompletion({
      messages: [{ role: 'user', content: prompt }],
      model: 'qwen/qwen3.8-27b',
      reasoning_effort: 'none',
      temperature: 0.2,
      max_tokens: 200,
      response_format: { type: 'json_object' },
    });

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      const match = raw?.match(/\{[\s\S]*\}/);
      if (match) parsed = JSON.parse(match[0]);
      else throw new Error('No JSON in response');
    }

    // Clamp all values to 0-15
    const clamp = (scores) =>
      Object.fromEntries(
        categories.map((c) => [c, Math.max(0, Math.min(15, parseInt(scores?.[c], 10) || 0))])
      );

    return { user: clamp(parsed.user), bot: clamp(parsed.bot) };
  } catch (err) {
    console.error('[SoloAIReview] Error, using fallback scoring:', err);
    return fallbackScoring(letter, categories, userAnswers, botAnswers);
  }
}

// Length-based fallback when AI is unavailable — accepts anything starting with the right letter
function fallbackScoring(letter, categories, userAnswers, botAnswers) {
  const score = (answer) => {
    const w = answer?.trim() || '';
    if (!w || w[0]?.toUpperCase() !== letter.toUpperCase()) return 0;
    if (w.length <= 4) return 4;
    if (w.length <= 6) return 7;
    if (w.length <= 9) return 10;
    return 13;
  };

  const user = Object.fromEntries(categories.map((c) => [c, score(userAnswers[c])]));
  const bot  = Object.fromEntries(categories.map((c) => [c, score(botAnswers[c])]));

  // Duplicate penalty
  categories.forEach((c) => {
    const ua = userAnswers[c]?.trim().toLowerCase();
    const ba = botAnswers[c]?.trim().toLowerCase();
    if (ua && ba && ua === ba && user[c] > 0 && bot[c] > 0) {
      user[c] = Math.floor(user[c] * 0.5);
      bot[c]  = Math.floor(bot[c]  * 0.5);
    }
  });

  return { user, bot };
}
